import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config';
import type { UserData } from './types';

// Fields that are safe to expose to any signed-in user (or, once mirrored,
// any visitor at all) — everything else on a /users/{uid} doc (email,
// address, stripeAccountId/Status, stripeCustomerId, ...) stays owner-only.
// Keep this in sync with the field allowlist on publicProfiles/{userId} in
// firestore.rules.
const PUBLIC_PROFILE_FIELDS = [
  'displayName', 'firstName', 'lastName', 'photoURL', 'coverImage',
  'biography', 'introVideo', 'social', 'path', 'allowEngagement',
  'allowFreeFollowing', 'denomination', 'churchName', 'churchLink',
  'churchLocation', 'churchId', 'donationImpact', 'userType',
] as const;

export type PublicProfile = Pick<UserData, typeof PUBLIC_PROFILE_FIELDS[number]>;

function extractPublicFields(data: Record<string, any>): PublicProfile {
  const result: Record<string, any> = {};
  for (const field of PUBLIC_PROFILE_FIELDS) {
    if (data[field] !== undefined) result[field] = data[field];
  }
  return result as PublicProfile;
}

/**
 * Mirrors the public-safe subset of a user's profile to publicProfiles/{uid}
 * (see DF-185/DF-190). Best-effort: a sync failure must not fail the caller's
 * own profile save, so errors are logged, not thrown.
 */
export async function syncPublicProfile(userId: string, fullData: Record<string, any>): Promise<void> {
  try {
    await setDoc(doc(db, 'publicProfiles', userId), {
      ...extractPublicFields(fullData),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.error('Error syncing public profile:', error);
  }
}

/**
 * Reads a user's public-safe fields for cross-user display (post author info,
 * mission page creator info, dashboard creator names). Prefers
 * publicProfiles/{uid} (PII-free, readable by anyone); falls back to the
 * legacy users/{uid} doc for a creator who has not saved their profile since
 * this split shipped -- that fallback still requires the users/{uid} rule to
 * allow it and does not itself reduce that doc's exposure.
 */
export async function getPublicProfile(userId: string): Promise<PublicProfile | null> {
  const directSnap = await getDoc(doc(db, 'publicProfiles', userId));
  if (directSnap.exists()) return directSnap.data() as PublicProfile;

  try {
    const legacySnap = await getDoc(doc(db, 'users', userId));
    if (legacySnap.exists()) return extractPublicFields(legacySnap.data());
  } catch (_) {
    // Not readable under the current rules (not a creator, or already locked down) -- no profile to show.
  }
  return null;
}
